import {
  CustomAdapter,
  CustomDateParserFormatter,
} from './utils/datepicker-adapter';
import { getActiveLocale } from './shared/locale.util';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { setTranslateServiceRef } from './shared/translation-bridge';
import { DataSharedStorageService } from './services/data-shared-storage.service';

const SUPPORTED_LANGUAGES = ['fr', 'en', 'pt'];

export function initializeTranslations(
  translateService: TranslateService,
  dataSharedStorageService: DataSharedStorageService
) {
  return async () => {
    setTranslateServiceRef(translateService);
    translateService.addLangs(SUPPORTED_LANGUAGES);
    translateService.setDefaultLang('fr');
    const savedLanguage = dataSharedStorageService.getItem('currentLanguage');
    const initialLanguage = SUPPORTED_LANGUAGES.includes(savedLanguage) ? savedLanguage : 'fr';
    await firstValueFrom(translateService.use(initialLanguage));
  };
}
import {
  NgbCollapseModule,
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDropdownModule,
  NgbModule,
  NgbToastModule,
} from '@ng-bootstrap/ng-bootstrap';
import {
  NgxBootstrapIconsModule,
  archive,
  arrowClockwise,
  arrowLeftRight,
  bank,
  boxes,
  calendar3,
  caretDownFill,
  caretUpFill,
  chevronDoubleLeft,
  chevronDoubleRight,
  chevronDown,
  chevronLeft,
  chevronRight,
  clipboard2Check,
  creditCard,
  currencyDollar,
  currencyExchange,
  dash,
  diagram2,
  envelopeOpen,
  exclamationCircle,
  fileEarmarkText,
  fileText,
  flag,
  gear,
  graphUp,
  grid,
  house,
  key,
  list,
  people,
  personBadge,
  plus,
  question,
  shop,
  speedometer,
  truck,
  truckFlatbed,
  map,
  columnsGap,
  pinMap,
  chatDots,
  table,
  journalCheck,
  translate
} from 'ngx-bootstrap-icons';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { EmptyCardModule } from './components/empty-card/empty-card.module';
import { ErrorInterceptorService } from './services/error-interceptor.service';
import { ForgotPasswordComponent } from './pages/forgot-password/forgot-password.component';
import { GoBackModule } from './components/go-back/go-back.module';
import { GoalsModule } from './pages/goals/goals.module';
import { HomeButtonCardComponent } from './pages/home/components/home-button-card/home-button-card.component';
import { HomeComponent } from './pages/home/home.component';
import { JwtInterceptorService } from './services/jwt-interceptor.service';
import { IsGuardedModule } from './components/is-guarded/is-guarded.module';
import { LayoutComponent } from './layout/layout.component';
import { LoadingsComponent } from './pages/loadings/loadings.component';
import { LoginComponent } from './pages/login/login.component';
import { NavLinkComponent } from './components/nav-link/nav-link.component';
import { NewOrderComponent } from './pages/order/new-order/new-order.component';
import { NgxSpinnerModule } from 'ngx-spinner';
import { OrderComponent } from './pages/order/order.component';
import { OthersComponent } from './pages/others/others.component';
import { OthersModule } from './pages/others/others.module';
import { PasswordStrengthComponent } from './components/password-strength/password-strength.component';
import { PerformancesModule } from './pages/performances/performances.module';
import { ResetPasswordComponent } from './pages/reset-password/reset-password.component';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { TableModule } from './components/table/table.module';
import { ToastComponent } from './components/toast/toast.component';
import { ToastsModule } from './components/toasts/toasts.module';
import { TransactionsForBillingComponent } from './pages/transactions/transactions-for-billing/transactions-for-billing.component';
import localeFr from '@angular/common/locales/fr';
import { registerLocaleData } from '@angular/common';
import { ClustersModule } from './pages/clusters/clusters.module';
import { OrderModule } from './pages/order/order.module';
import { ToursModule } from './pages/tours/tours.module';
import { CountriesModule } from './pages/countries/countries.module';
import { FirstLoginPasswordChangeComponent } from './pages/first-login-password-change/first-login-password-change.component';
import { translateHttpLoaderFactory } from './shared/translate-http-loader.factory';

registerLocaleData(localeFr);

const icons = {
  archive,
  graphUp,
  house,
  calendar3,
  clipboard2Check,
  creditCard,
  truck,
  truckFlatbed,
  dash,
  diagram2,
  envelopeOpen,
  fileEarmarkText,
  plus,
  question,
  boxes,
  exclamationCircle,
  arrowClockwise,
  arrowLeftRight,
  people,
  shop,
  key,
  personBadge,
  speedometer,
  caretUpFill,
  caretDownFill,
  gear,
  currencyDollar,
  fileText,
  currencyExchange,
  chevronRight,
  chevronLeft,
  chevronDown,
  chevronDoubleLeft,
  chevronDoubleRight,
  flag,
  grid,
  bank,
  list,
  map,
  columnsGap,
  pinMap,
  chatDots,
  table,
  journalCheck,
  translate
};

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    HomeComponent,
    LayoutComponent,
    HomeButtonCardComponent,
    LoadingsComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    PasswordStrengthComponent,
    NavLinkComponent,
    TransactionsForBillingComponent,
    ToastComponent,
    OthersComponent,
    FirstLoginPasswordChangeComponent,
  ],
  imports: [
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    ClustersModule,
    CountriesModule,
    EmptyCardModule,
    FormsModule,
    GoalsModule,
    GoBackModule,
    HttpClientModule,
    NgbCollapseModule,
    NgbDropdownModule,
    NgbModule,
    NgbToastModule,
    NgxBootstrapIconsModule.pick(icons),
    NgxSpinnerModule,
    OrderModule,
    OthersModule,
    ToursModule,
    PerformancesModule,
    ReactiveFormsModule,
    SweetAlert2Module.forRoot(),
    IsGuardedModule,
    TableModule,
    ToastsModule,
    TranslateModule.forRoot({
      defaultLanguage: 'fr',
      loader: {
        provide: TranslateLoader,
        useFactory: translateHttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTranslations,
      deps: [TranslateService, DataSharedStorageService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptorService,
      multi: true,
    },
    { provide: LOCALE_ID, useFactory: getActiveLocale },
    { provide: NgbDateAdapter, useClass: CustomAdapter },
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
